//@flow
import * as React from 'react';
import sortBy from 'lodash/sortBy';
import { translate, type TFunction } from 'react-i18next';
import { selectFactory, SelectTypes } from 'common/components/core/select';
import { memoizedImmutable } from 'common/utils/Memoizer';
import { TeamOptionRenderer, MultiTeamValue } from './TeamRenderer';
import { TeamId } from 'common/models/internal/TeamId';
import { Team } from 'common/models/internal/Team';
import type { List } from 'immutable';
import { TeamStatusEnum } from 'common/enums/TeamEnum';

const Select = selectFactory({
    Option: TeamOptionRenderer,
    MultiValue: MultiTeamValue
});

type TeamOptionType = {
    value: string,
    label: string,
    color?: string
};

type Props = {
    theme?: Object,
    className?: string,
    selectType?: string,
    value: ?List<TeamId>,
    teams: List<Team>,
    options?: Array<TeamOptionType>,
    t: TFunction
};

class MultiTeamSelect extends React.PureComponent<Props, void> {
    @memoizedImmutable()
    getTeamIdsOptions(teams: List<Team>): Array<TeamOptionType> {
        let options = [];
        if (teams) {
            teams.forEach(team => {
                if (team.status === TeamStatusEnum.Inactive) {
                    return;
                }
                options.push({
                    value: team.teamId,
                    label: team.name || team.teamId,
                    color: team.color,
                    status: team.status
                });
            });
        }
        options = sortBy(options, option => option.label.toUpperCase());
        return options;
    }

    @memoizedImmutable()
    getTeamIdsValues(
        value: ?List<TeamId>,
        teams: List<Team>
    ): Array<TeamOptionType> {
        let selectedTeamOptions = [];
        const teamOptions = this.getTeamIdsOptions(teams);
        if (value) {
            selectedTeamOptions = value.reduce(
                (allSelectedOptions, currentTeamId) => {
                    let newAllSelectedOptions = [...allSelectedOptions];
                    const { teamId } = currentTeamId;
                    if (teamId) {
                        let option = teamOptions.find(
                            team => team.value === teamId
                        );
                        if (!option) {
                            option = {
                                value: teamId,
                                label: teamId
                            };
                        }
                        newAllSelectedOptions = [
                            ...newAllSelectedOptions,
                            option
                        ];
                    }
                    return newAllSelectedOptions;
                },
                []
            );
        }
        return selectedTeamOptions;
    }

    render() {
        const { theme, teams, selectType, t, ...others } = this.props;
        let { options, value } = this.props;
        if (teams) {
            options = this.getTeamIdsOptions(teams);
            value = this.getTeamIdsValues(value, teams);
        }
        return (
            <Select
                {...others}
                closeMenuOnSelect={false}
                theme={theme}
                selectType={selectType}
                options={options}
                isMulti
                openMenuOnFocus
                blurInputOnSelect={false}
                isSearchable
                placeholder={t('selectTeams')}
                value={value}
            />
        );
    }
}

MultiTeamSelect.defaultProps = {
    selectType: SelectTypes.Outline
};

export default translate()(MultiTeamSelect);
